import axios from "axios";
import { api, baseURL, aplicarIdioma } from "./Api";

// aplicarIdioma: esta instância não passa pelo `api`, então precisa do
// mesmo interceptor para o cadastro sair no idioma escolhido
const apiUpload = aplicarIdioma(axios.create({ baseURL }));

// ─── Search establishments (public) ──────────────────────────────────────────

export const searchEstabilishments = (q: string) =>
  api.get(`/pt_BR/estabilishment/search?q=${encodeURIComponent(q)}`);

/**
 * Vincula um novo estabelecimento à conta do usuário já logado.
 * Diferente do cadastro inicial, aqui só o vínculo é criado.
 */
export const addEstabilishmentToAccount = (estabilishmentId: string) =>
  api.post(
    "/pt_BR/user_estabilishment/add",
    { estabilishment: estabilishmentId },
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
  );

/**
 * Informa quais dos estabelecimentos consultados já estão vinculados a outro
 * usuário. Usado no passo 2 do cadastro para avisar na hora da escolha, em vez
 * de falhar com 409 só no envio final.
 */
export const checkEstabilishmentsTaken = (ids: string[]) =>
  api.get(
    `/pt_BR/user_estabilishment/check_estabilishments?ids=${encodeURIComponent(ids.join(","))}`,
  );

/** Verifica se um e-mail já está cadastrado (validação inline do formulário) */
export const checkEmailExists = (email: string) =>
  api.get(`/pt_BR/user_estabilishment/check_email?email=${encodeURIComponent(email)}`);

// ─── Register establishment user (public) ─────────────────────────────────────

/** Planos disponíveis no cadastro. Pulse e Wave pertencem à família Showcase. */
export type RegisterPlan = "Insight" | "Connection" | "Pulse" | "Wave";

export interface IPostUserEstabilishment {
  email: string;
  password: string;
  /** Plano escolhido. Insight = 3 meses grátis sem cartão; Connection = 15 dias de degustação com cartão. */
  plan?: RegisterPlan;
  /**
   * Vínculo da pessoa com o estabelecimento: owner | employee | agency.
   * Código, e não rótulo: o painel tem português e inglês, e o que vai para
   * o banco precisa ser o mesmo nos dois idiomas.
   */
  relationship?: string | null;
  /** Função (colaborador) ou nome da agência. Vazio para proprietário. */
  relationshipDetail?: string | null;
  /** CNPJ do estabelecimento, só dígitos e letras (sem máscara). */
  cnpj?: string | null;
  profile: {
    fullName: string;
    instagram?: string | null;
    site?: string | null;
    address?: {
      postalCode: string;
      street: string;
      number: string;
      complement?: string;
      district: string;
      city: string;
      state: string;
      stateShort?: string;
      country?: string;
    };
  };
  estabilishments: string[];
}

/** Retorno do cadastro — o backend informa se precisa passar pela Stripe */
export interface IRegisterResponse {
  data: {
    code: number;
    message: string;
    data: {
      user_id: string;
      userEstabilishmentIds: string[];
      planType: "insight" | "connection" | "pulse" | "wave" | "none";
      planName: string | null;
      trialEndsAt: string | null;
      /** true → redirecionar para o checkout da Stripe após os uploads */
      requiresCard: boolean;
      documentsStatus: string;
    };
  };
}

/**
 * Cadastra o usuário COM os documentos, num request só.
 *
 * Antes eram duas etapas — criar a conta e depois subir os arquivos, um
 * request por arquivo × estabelecimento. Qualquer falha no meio deixava a
 * conta criada sem documento, e a pessoa não conseguia nem repetir: o e-mail
 * já estava em uso. Agora ou entra tudo, ou não entra nada.
 *
 * Vai como multipart/form-data. Os campos aninhados usam a notação de
 * colchetes que o PHP remonta em array (profile[address][street]).
 *
 * Campos opcionais vazios são OMITIDOS de propósito: o form do Symfony trata
 * campo ausente como null, enquanto string vazia gravaria "" no lugar de NULL.
 */
export const postUserEstabilishment = (
  body: IPostUserEstabilishment,
  documents: File[],
  selfie?: File | null,
) => {
  const fd = new FormData();

  fd.append("email", body.email);
  fd.append("password", body.password);
  if (body.plan) fd.append("plan", body.plan);
  if (body.relationship) fd.append("relationship", body.relationship);
  if (body.relationshipDetail)
    fd.append("relationshipDetail", body.relationshipDetail);
  if (body.cnpj) fd.append("cnpj", body.cnpj);

  fd.append("profile[fullName]", body.profile.fullName);
  if (body.profile.instagram) fd.append("profile[instagram]", body.profile.instagram);
  if (body.profile.site) fd.append("profile[site]", body.profile.site);

  const address = body.profile.address;
  if (address) {
    fd.append("profile[address][postalCode]", address.postalCode ?? "");
    fd.append("profile[address][street]", address.street ?? "");
    fd.append("profile[address][number]", address.number ?? "");
    fd.append("profile[address][complement]", address.complement ?? "");
    fd.append("profile[address][district]", address.district ?? "");
    fd.append("profile[address][city]", address.city ?? "");
    fd.append("profile[address][state]", address.state ?? "");
    fd.append("profile[address][stateShort]", address.stateShort ?? "");
    fd.append("profile[address][country]", address.country ?? "Brasil");
  }

  body.estabilishments.forEach((id) => fd.append("estabilishments[]", id));

  documents.forEach((file) => fd.append("documents[]", file));
  if (selfie) fd.append("selfie", selfie);

  // sem headers: o browser monta o Content-Type com o boundary
  return apiUpload.post("/pt_BR/user_estabilishment", fd);
};

// ─── Upload attachment (public — no auth required) ────────────────────────────
//
// Não é mais usado no cadastro: os documentos vão junto em
// postUserEstabilishment. Continua aqui para reenvio depois de uma recusa e
// para quem acrescenta uma unidade a uma conta que já existe.

/** Tipo do anexo: documento do estabelecimento ou selfie com documento pessoal */
export type AttachmentType = "document" | "selfie";

export const postUserEstabilishmentAttachment = (
  userEstabilishmentId: string,
  file: File,
  type: AttachmentType = "document",
) => {
  const formData = new FormData();
  formData.append("uploadFile", file);

  // ID e tipo enviados como query string — garante que cheguem
  // independente do Content-Type do multipart
  return apiUpload.post(
    `/pt_BR/upload_user_estabilishment_attachment` +
      `?userEstabilishment=${encodeURIComponent(userEstabilishmentId)}` +
      `&type=${encodeURIComponent(type)}`,
    formData,
  );
};